import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Location.css";

class Location extends Component {
  state = {
    activeTab: "main", 
    mainLocations: [],
    subLocations: [],
    
    mainFormData: {
      id: null,
      location_main_name: "",
      location_main_status: "1",
    },
    subFormData: {
      id: null,
      location_main_id: "",
      location_sub_name: "",
      location_sub_status: "1",
    },

    deleteId: null,
    deleteType: "main", // "main" or "sub"
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",

    searchTerm: "",
    statusFilter: "all",
    mainFilter: "all", // Filter sub-locations by main location ID
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchAllData();
  }

  /** 🔍 Cleanly extract specific validation error messages from CodeIgniter response */
  extractErrorMessage(err, defaultMsg) {
    const resData = err.response?.data;
    if (resData?.messages) {
      if (typeof resData.messages === "object") {
        const firstKey = Object.keys(resData.messages)[0];
        if (firstKey) {
          return resData.messages[firstKey];
        }
      } else if (typeof resData.messages === "string") {
        return resData.messages;
      }
    }
    if (resData?.message) {
      return resData.message;
    }
    return defaultMsg;
  }

  fetchAllData = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const [resMain, resSub] = await Promise.all([
        axios.get("posts/location_main"),
        axios.get("posts/location_sub"),
      ]);

      const mainData = resMain.data?.data || [];
      const subData = resSub.data?.data || [];

      this.setState({
        mainLocations: Array.isArray(mainData) ? mainData : [],
        subLocations: Array.isArray(subData) ? subData : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: this.extractErrorMessage(err, "Failed to load locations."),
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleTabChange = (tab) => {
    this.setState({
      activeTab: tab,
      searchTerm: "",
      statusFilter: "all",
      mainFilter: "all",
      currentPage: 1,
      error: "",
    });
  };

  handleMainChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      mainFormData: { ...prev.mainFormData, [name]: value },
      error: "",
    }));
  };

  handleSubChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      subFormData: { ...prev.subFormData, [name]: value },
      error: "",
    }));
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleMainFilterChange = (e) => {
    this.setState({ mainFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", mainFilter: "all", currentPage: 1 });
  };

  openModal = (mode = "add", itemObj = null) => {
    const { activeTab } = this.state;
    const modalId = activeTab === "main" ? "locationMainModal" : "locationSubModal";
    const modalElement = document.getElementById(modalId);
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);

    if (activeTab === "main") {
      this.setState(
        {
          modalMode: mode,
          mainFormData:
            mode === "edit" && itemObj
              ? {
                  id: itemObj.id,
                  location_main_name: itemObj.location_main_name || "",
                  location_main_status: itemObj.location_main_status?.toString() || "1",
                }
              : {
                  id: null,
                  location_main_name: "",
                  location_main_status: "1",
                },
          error: "",
        },
        () => modal.show()
      );
    } else {
      this.setState(
        {
          modalMode: mode,
          subFormData:
            mode === "edit" && itemObj
              ? {
                  id: itemObj.id,
                  location_main_id: itemObj.location_main_id || "",
                  location_sub_name: itemObj.location_sub_name || "",
                  location_sub_status: itemObj.location_sub_status?.toString() || "1",
                }
              : {
                  id: null,
                  location_main_id: "",
                  location_sub_name: "",
                  location_sub_status: "1",
                },
          error: "",
        },
        () => modal.show()
      );
    }
  };

  closeModal = () => {
    const { activeTab } = this.state;
    const modalId = activeTab === "main" ? "locationMainModal" : "locationSubModal";
    const modalElement = document.getElementById(modalId);
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      mainFormData: { id: null, location_main_name: "", location_main_status: "1" },
      subFormData: { id: null, location_main_id: "", location_sub_name: "", location_sub_status: "1" },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  handleSubmitMain = async (e) => {
    e.preventDefault();
    const { mainFormData, modalMode } = this.state;
    const name = mainFormData.location_main_name.trim();

    if (!name) {
      return this.setState({ error: "Location Main Name is required." });
    }

    this.setState({ submitting: true, error: "" });

    const payload = {
      location_main_name: name,
      location_main_status: mainFormData.location_main_status,
    };

    try {
      if (modalMode === "add") {
        await axios.post("posts/location_main", payload);
      } else {
        await axios.put(`posts/location_main/${mainFormData.id}`, payload);
      }

      await this.fetchAllData();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Main Location added successfully!" : "Main Location updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error: this.extractErrorMessage(err, "Error saving main location."),
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleSubmitSub = async (e) => {
    e.preventDefault();
    const { subFormData, modalMode } = this.state;
    const mainId = subFormData.location_main_id;
    const subName = subFormData.location_sub_name.trim();

    if (!mainId) {
      return this.setState({ error: "Please select a Main Location." });
    }
    if (!subName) {
      return this.setState({ error: "Sub Location Name is required." });
    }

    this.setState({ submitting: true, error: "" });

    const payload = {
      location_main_id: mainId,
      location_sub_name: subName,
      location_sub_status: subFormData.location_sub_status,
    };

    try {
      if (modalMode === "add") {
        await axios.post("posts/location_sub", payload);
      } else {
        await axios.put(`posts/location_sub/${subFormData.id}`, payload);
      }

      await this.fetchAllData();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Sub Location added successfully!" : "Sub Location updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error: this.extractErrorMessage(err, "Error saving sub location."),
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id, type) => {
    this.setState({ deleteId: id, deleteType: type });
    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId, deleteType } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });
    const endpoint = deleteType === "main" ? `posts/location_main/${deleteId}` : `posts/location_sub/${deleteId}`;

    try {
      await axios.delete(endpoint);
      await this.fetchAllData();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: `${deleteType === "main" ? "Main" : "Sub"} Location deleted successfully!`,
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete location.",
        text: this.extractErrorMessage(err, "Please try again."),
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredData = () => {
    const { activeTab, mainLocations, subLocations, searchTerm, statusFilter, mainFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    if (activeTab === "main") {
      return mainLocations.filter((item) => {
        const name = (item.location_main_name || "").toLowerCase();
        const status = item.location_main_status?.toString() || "0";
        const matchesSearch = !keyword || name.includes(keyword);
        const matchesStatus = statusFilter === "all" || status === statusFilter;
        return matchesSearch && matchesStatus;
      });
    } else {
      return subLocations.filter((item) => {
        const name = (item.location_sub_name || "").toLowerCase();
        const parentName = (item.location_main_name || "").toLowerCase();
        const status = item.location_sub_status?.toString() || "0";
        const mainId = item.location_main_id?.toString() || "";

        const matchesSearch = !keyword || name.includes(keyword) || parentName.includes(keyword);
        const matchesStatus = statusFilter === "all" || status === statusFilter;
        const matchesMain = mainFilter === "all" || mainId === mainFilter.toString();

        return matchesSearch && matchesStatus && matchesMain;
      });
    }
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(this.getFilteredData().length / itemsPerPage));
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  render() {
    const {
      activeTab,
      mainLocations,
      subLocations,
      mainFormData,
      subFormData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      mainFilter,
      currentPage,
      itemsPerPage,
      deleteType,
    } = this.state;

    const filteredData = this.getFilteredData();
    const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentData = filteredData.slice(startIndex, startIndex + itemsPerPage);
    const startRecord = filteredData.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredData.length);
    const pageNumbers = this.getPageNumbers(totalPages);

    const mainActiveCount = mainLocations.filter((a) => a.location_main_status?.toString() === "1").length;
    const subActiveCount = subLocations.filter((a) => a.location_sub_status?.toString() === "1").length;

    return (
      <div className="pc-container location-page">
        <div className="pc-content">
          <div className="location-shell">
            {/* Header */}
            <div className="location-topbar">
              <div>
                <div className="location-breadcrumb">
                  <span>Settings</span>
                  <i className="ph ph-caret-right" />
                  <strong>Locations</strong>
                </div>
                <h2 className="location-title">Location Management</h2>
                <p className="location-subtitle">
                  Manage main locations and hierarchical sub-locations for your admin panel.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>{activeTab === "main" ? "New Main Location" : "New Sub Location"}</span>
              </button>
            </div>

            {/* Summary Grid */}
            <div className="location-summary-grid">
              <div className="location-summary-card">
                <div className="location-summary-icon blue">
                  <i className="ph ph-map-pin" />
                </div>
                <div>
                  <span className="summary-label">Main Locations</span>
                  <strong>{mainLocations.length}</strong>
                </div>
              </div>

              <div className="location-summary-card">
                <div className="location-summary-icon green">
                  <i className="ph ph-git-fork" />
                </div>
                <div>
                  <span className="summary-label">Sub Locations</span>
                  <strong>{subLocations.length}</strong>
                </div>
              </div>

              <div className="location-summary-card">
                <div className="location-summary-icon pink">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active Items</span>
                  <strong>{activeTab === "main" ? mainActiveCount : subActiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="location-tabs-nav">
              <button
                type="button"
                className={`location-tab-btn ${activeTab === "main" ? "active" : ""}`}
                onClick={() => this.handleTabChange("main")}
              >
                <i className="ph ph-map-pin" />
                Main Locations ({mainLocations.length})
              </button>
              <button
                type="button"
                className={`location-tab-btn ${activeTab === "sub" ? "active" : ""}`}
                onClick={() => this.handleTabChange("sub")}
              >
                <i className="ph ph-git-fork" />
                Sub Locations ({subLocations.length})
              </button>
            </div>

            {/* Main Card */}
            <div className="location-card">
              <div className="location-card-header">
                <div>
                  <h4>{activeTab === "main" ? "Main Location List" : "Sub Location List"}</h4>
                  <p>
                    {filteredData.length} result{filteredData.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="location-toolbar">
                <div className="location-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="location-search"
                    placeholder={activeTab === "main" ? "Search main location..." : "Search sub or main location..."}
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="location-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                {activeTab === "sub" && (
                  <div className="location-filter-group">
                    <label htmlFor="mainLocationFilter">Main Location</label>
                    <select
                      id="mainLocationFilter"
                      className="location-filter-select"
                      value={mainFilter}
                      onChange={this.handleMainFilterChange}
                    >
                      <option value="all">All Main Locations</option>
                      {mainLocations.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.location_main_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="location-filter-group">
                  <label htmlFor="locationStatusFilter">Status</label>
                  <select
                    id="locationStatusFilter"
                    className="location-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="location-filter-group">
                  <label htmlFor="locationRows">Rows</label>
                  <select
                    id="locationRows"
                    className="location-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all" || mainFilter !== "all") && (
                  <button
                    type="button"
                    className="location-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="location-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchAllData}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="location-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading locations...</span>
                </div>
              ) : (
                <>
                  <div className="location-table-wrap">
                    <table className="location-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          {activeTab === "main" ? (
                            <th>Main Location Name</th>
                          ) : (
                            <>
                              <th>Sub Location Name</th>
                              <th>Parent Main Location</th>
                            </>
                          )}
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.length ? (
                          currentData.map((item, index) => {
                            const isActive =
                              activeTab === "main"
                                ? item.location_main_status === "1" || item.location_main_status === 1
                                : item.location_sub_status === "1" || item.location_sub_status === 1;

                            return (
                              <tr key={item.id}>
                                <td className="col-number table-index">{startIndex + index + 1}</td>
                                {activeTab === "main" ? (
                                  <td>
                                    <div className="location-content-cell">
                                      <div className="location-badge-icon">
                                        <i className="ph ph-map-pin" />
                                      </div>
                                      <div>
                                        <span className="location-text-content">{item.location_main_name}</span>
                                        <small>ID #{item.id}</small>
                                      </div>
                                    </div>
                                  </td>
                                ) : (
                                  <>
                                    <td>
                                      <div className="location-content-cell">
                                        <div className="location-badge-icon">
                                          <i className="ph ph-git-fork" />
                                        </div>
                                        <div>
                                          <span className="location-text-content">{item.location_sub_name}</span>
                                          <small>ID #{item.id}</small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge bg-light text-dark fw-bold">
                                        {item.location_main_name || "N/A"}
                                      </span>
                                    </td>
                                  </>
                                )}
                                <td className="col-status">
                                  <span className={`location-status-badge ${isActive ? "active" : "inactive"}`}>
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="location-actions">
                                    <button
                                      type="button"
                                      className="location-action-btn edit"
                                      onClick={() => this.openModal("edit", item)}
                                      title="Edit location"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="location-action-btn delete"
                                      onClick={() => this.confirmDelete(item.id, activeTab)}
                                      title="Delete location"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={activeTab === "main" ? "4" : "5"}>
                              <div className="location-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-map-pin" />
                                </div>
                                <h5>No {activeTab === "main" ? "main" : "sub"} locations found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all" || mainFilter !== "all"
                                    ? "Try changing your search query or filters."
                                    : `Add your first ${activeTab === "main" ? "main" : "sub"} location to get started.`}
                                </p>
                                {searchTerm || statusFilter !== "all" || mainFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="location-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Location
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredData.length > 0 && (
                    <div className="location-pagination-wrap">
                      <div className="location-pagination-info">
                        Showing <strong>{startRecord}</strong> to <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredData.length}</strong> items
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Location pagination">
                          <ul className="location-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li key={page} className={safeCurrentPage === page ? "active" : ""}>
                                  <button type="button" onClick={() => this.goToPage(page)}>
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li className={safeCurrentPage === totalPages ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Main Location Modal */}
        <div className="modal fade" id="locationMainModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content location-modal">
              <form onSubmit={this.handleSubmitMain}>
                <div className="modal-header location-modal-header">
                  <div>
                    <span className="location-modal-kicker">{modalMode === "add" ? "Create" : "Update"}</span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Main Location" : "Edit Main Location"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="location-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body location-modal-body">
                  {error && (
                    <div className="location-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="location-form-group">
                    <label htmlFor="locationMainName">
                      Main Location Name <span>*</span>
                    </label>
                    <div className="location-input-wrap">
                      <i className="ph ph-map-pin" />
                      <input
                        type="text"
                        id="locationMainName"
                        name="location_main_name"
                        className="location-input"
                        placeholder="Enter main location name..."
                        value={mainFormData.location_main_name}
                        onChange={this.handleMainChange}
                      />
                    </div>
                  </div>

                  <div className="location-form-group mb-0">
                    <label>Status</label>
                    <div className="location-status-options">
                      <label
                        className={`status-option ${
                          mainFormData.location_main_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="location_main_status"
                          value="1"
                          checked={mainFormData.location_main_status === "1"}
                          onChange={this.handleMainChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>
                      <label
                        className={`status-option ${
                          mainFormData.location_main_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="location_main_status"
                          value="0"
                          checked={mainFormData.location_main_status === "0"}
                          onChange={this.handleMainChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="location-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="bookly-primary-btn" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} />
                        {modalMode === "add" ? "Add Main Location" : "Update Main Location"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Add/Edit Sub Location Modal */}
        <div className="modal fade" id="locationSubModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content location-modal">
              <form onSubmit={this.handleSubmitSub}>
                <div className="modal-header location-modal-header">
                  <div>
                    <span className="location-modal-kicker">{modalMode === "add" ? "Create" : "Update"}</span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Sub Location" : "Edit Sub Location"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="location-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body location-modal-body">
                  {error && (
                    <div className="location-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="location-form-group">
                    <label htmlFor="locationMainId">
                      Select Main Location <span>*</span>
                    </label>
                    <div className="location-input-wrap">
                      <i className="ph ph-map-pin" />
                      <select
                        id="locationMainId"
                        name="location_main_id"
                        className="location-select"
                        value={subFormData.location_main_id}
                        onChange={this.handleSubChange}
                      >
                        <option value="">-- Choose Main Location --</option>
                        {mainLocations.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.location_main_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="location-form-group">
                    <label htmlFor="locationSubName">
                      Sub Location Name <span>*</span>
                    </label>
                    <div className="location-input-wrap">
                      <i className="ph ph-git-fork" />
                      <input
                        type="text"
                        id="locationSubName"
                        name="location_sub_name"
                        className="location-input"
                        placeholder="Enter sub location name..."
                        value={subFormData.location_sub_name}
                        onChange={this.handleSubChange}
                      />
                    </div>
                  </div>

                  <div className="location-form-group mb-0">
                    <label>Status</label>
                    <div className="location-status-options">
                      <label
                        className={`status-option ${
                          subFormData.location_sub_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="location_sub_status"
                          value="1"
                          checked={subFormData.location_sub_status === "1"}
                          onChange={this.handleSubChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>
                      <label
                        className={`status-option ${
                          subFormData.location_sub_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="location_sub_status"
                          value="0"
                          checked={subFormData.location_sub_status === "0"}
                          onChange={this.handleSubChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="location-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="bookly-primary-btn" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} />
                        {modalMode === "add" ? "Add Sub Location" : "Update Sub Location"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete {deleteType === "main" ? "Main" : "Sub"} Location?</h5>
                <p>This location will be permanently removed. This action cannot be undone.</p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Location;